export enum SiteStatus {
  Active = 1,
  Inactive = 2,
}

export interface IOperationalOverview {
  activeSiteCount: number
  totalTeamCount: number
  totalAssignedUserCount: number
  totalUnassignedUserCount: number
  teamWithoutSupervisorCount: number
}

export interface IOperationalSite {
  id: number
  name: string
  status: SiteStatus
  createdAt: string
  teamCount: number
  memberCount: number
}

export interface IGetOperationalSitesParams {
  page: number
  limit: number
  q?: string
}

export enum TeamType {
  Operations = 1,
  Maintenance = 2,
  Support = 3,
  Logistics = 4,
  Security = 5,
  Admin = 6,
}

export const TEAM_TYPE_LABEL: Record<TeamType, string> = {
  [TeamType.Operations]: 'Operations',
  [TeamType.Maintenance]: 'Maintenance',
  [TeamType.Support]: 'Support',
  [TeamType.Logistics]: 'Logistics',
  [TeamType.Security]: 'Security',
  [TeamType.Admin]: 'Admin',
}

interface TeamTypeAccentTheme {
  blueChart: string
  colorOrange: string
  colorPurple: string
  colorGreen: string
  colorRed: string
  greyText: string
}

export const getTeamTypeAccent = (theme: TeamTypeAccentTheme, type: TeamType): string => {
  switch (type) {
    case TeamType.Operations:
      return theme.blueChart
    case TeamType.Maintenance:
      return theme.colorOrange
    case TeamType.Support:
      return theme.colorPurple
    case TeamType.Logistics:
      return theme.colorGreen
    case TeamType.Security:
      return theme.colorRed
    case TeamType.Admin:
      return theme.greyText
  }
}

export interface IUserSimple {
  id: number
  firstName: string
  lastName: string
}

export interface IOperationalTeamSupervisor {
  id: number
  user: IUserSimple
}

export interface ISimpleDepartment {
  id: number
  name: string
  status: number
}

export interface ISimpleRole {
  id: number
  name: string
}

export interface IOperationalOrgUser {
  id: number
  firstName: string
  lastName: string
  department: ISimpleDepartment | null
  currentRole: ISimpleRole | null
}

export interface IOperationalOrganogramTeam {
  id: number
  name: string
  type: TeamType
  supervisors: IOperationalTeamSupervisor[]
  members: IOperationalOrgUser[]
  children: IOperationalOrganogramTeam[]
}

export interface IOperationalSiteOrganogram {
  teams: IOperationalOrganogramTeam[]
}

export interface ICreateOperationalSiteRequest {
  name: string
}

export interface IUpdateOperationalSiteRequest {
  id: number
  name: string
  status: SiteStatus
  reason: string
}

export interface IOperationalSiteSimple {
  id: number
  name: string
}

export interface IOperationalTeamSimple {
  id: number
  name: string
  type: TeamType
}

export interface IOperationalTeam {
  id: number
  name: string
  type: TeamType
  operationalSite: IOperationalSiteSimple | null
  parentTeam: IOperationalTeamSimple | null
  supervisors: IOperationalTeamSupervisor[]
  createdAt: string
  memberCount: number
}

export interface IGetOperationalTeamsParams {
  page: number
  limit: number
  q?: string
  siteIds?: string
  parentTeamIds?: string
  types?: string
  supervisorIds?: string
}

export interface ICreateOperationalTeamRequest {
  operationalSiteId: number
  name: string
  type: TeamType
  parentTeamId?: number
  supervisorIds?: number[]
}

export interface IBasicUserSimple {
  id: number
  fullName: string
}

export interface IOperationalTeamMemberAssignment {
  id: number
  user: IOperationalOrgUser | null
  startDate: string
  endDate: string | null
  reason: string
  createdAt: string
}

export interface IOperationalTeamDetails {
  id: number
  name: string
  type: TeamType
  operationalSite: IOperationalSiteSimple | null
  parentTeam: IOperationalTeamSimple | null
  supervisors: IOperationalTeamSupervisor[]
  createdAt: string
  currentMembers: IOperationalTeamMemberAssignment[]
  escalationChain: IOperationalTeamSupervisor[][]
}

export interface IPreassignUser {
  id: number
  firstName: string
  lastName: string
  department: ISimpleDepartment | null
  currentRole: ISimpleRole | null
}

export enum PreassignActionType {
  Assign = 1,
  Reassign = 2,
}

export interface IPreassignSummary {
  selectedUserCount: number
  readyUserCount: number
  conflictUserCount: number
  reassignmentUserCount: number
}

export interface IPreassignConflict {
  user: IPreassignUser
  reason: string
}

export interface IPreassignReassignment {
  user: IPreassignUser
  fromTeamName: string
  toTeamName: string
}

export interface IPreassignReady {
  user: IPreassignUser
  actionType: PreassignActionType
}

export interface IPreassignDataResponse {
  summary: IPreassignSummary
  conflicts: IPreassignConflict[]
  reassignments: IPreassignReassignment[]
  readyUsers: IPreassignReady[]
}

export type IGetPreassignDataResponse = IPreassignDataResponse

export interface IGetPreassignDataRequest {
  userIds: number[]
  operationalTeamId: number
  startDate: string
  endDate?: string
}

export interface ISuccessfulAssignment {
  id: number
  user: IUserSimple
  startDate: string
  endDate: string | null
}

export interface IUnsuccessfulAssignment {
  user: IUserSimple
}

export interface IAssignUsersToTeamResponse {
  successfulAssignments: ISuccessfulAssignment[]
  unsuccessfulAssignments: IUnsuccessfulAssignment[]
}

export interface IAssignUsersToTeamRequest {
  userIds: number[]
  operationalTeamId: number
  startDate: string
  endDate?: string
  reason: string
}


export interface IOperationalManagementState {
  isLoading: boolean
  isLoadingSites: boolean
  overview: IOperationalOverview | null
  sites: {
    data: IOperationalSite[]
    totalCount: number
    totalPages: number
  }
  organogramBySiteId: Record<number, IOperationalSiteOrganogram>
  loadingOrganogramSiteIds: number[]
  creatingSite: boolean
  updatingSite: boolean
  teams: {
    data: IOperationalTeam[]
    totalCount: number
    totalPages: number
  }
  isLoadingTeams: boolean
  creatingTeam: boolean
  simpleSites: IOperationalSiteSimple[]
  simpleTeams: IOperationalTeamSimple[]
  simpleTeamsBySite: Record<number, IOperationalTeamSimple[]>
  supervisorCandidates: IBasicUserSimple[]
  teamDetailsById: Record<number, IOperationalTeamDetails>
  loadingTeamDetailsIds: number[]
  assigningEmployees: boolean
}
